/**
 * <s-look-switcher> — swaps the product gallery between styled looks (e.g. Male / Female).
 *
 * Lives inside <s-product-gallery>. Slides carry data-look="<key>"; slides without
 * data-look are shared and always stay visible. Option buttons carry
 * data-look-option="<key>" and aria-pressed. The host's data-look reflects the
 * active look so CSS (the sliding pill) can follow it.
 *
 * Motion: outgoing photos fade out, incoming photos fade and rise in with a
 * short stagger. Reduced motion swaps instantly.
 */
class SLookSwitcher extends HTMLElement {
  #ac;
  #busy = false;

  connectedCallback() {
    this.track = this.closest('s-product-gallery')?.querySelector('[data-track]');
    this.options = [...this.querySelectorAll('[data-look-option]')];
    if (!this.track || this.options.length === 0) return;

    this.#ac = new AbortController();
    for (const option of this.options) {
      option.addEventListener('click', () => this.select(option.dataset.lookOption), { signal: this.#ac.signal });
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  async select(look) {
    if (!look || look === this.dataset.look || this.#busy) return;
    this.#busy = true;

    const slides = [...this.track.querySelectorAll(':scope > [data-look]')];
    const leaving = slides.filter((slide) => !slide.hidden);
    const entering = slides.filter((slide) => slide.dataset.look === look);

    this.dataset.look = look;
    for (const option of this.options) {
      option.setAttribute('aria-pressed', String(option.dataset.lookOption === look));
    }

    const motion = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const easing = getComputedStyle(this).getPropertyValue('--ease-ui').trim() || 'cubic-bezier(0.16, 1, 0.3, 1)';

    if (motion && leaving.length) {
      const outs = leaving.map(
        (slide) =>
          slide.animate([{ opacity: 1 }, { opacity: 0, transform: 'scale(0.985)' }], {
            duration: 180,
            easing,
            fill: 'forwards',
          }).finished
      );
      await Promise.allSettled(outs);
    }

    for (const slide of slides) {
      slide.hidden = slide.dataset.look !== look;
      for (const animation of slide.getAnimations()) animation.cancel();
    }

    // Start the new look from its first photo and let the gallery refresh its progress bar and buttons.
    this.track.scrollTo({ left: 0, behavior: 'auto' });
    this.track.dispatchEvent(new Event('scroll'));

    if (motion) {
      entering.forEach((slide, index) => {
        slide.animate(
          [
            { opacity: 0, transform: 'translateY(14px)' },
            { opacity: 1, transform: 'none' },
          ],
          { duration: 520, delay: index * 90, easing, fill: 'backwards' }
        );
      });
    }

    this.#busy = false;
  }
}

if (!customElements.get('s-look-switcher')) {
  customElements.define('s-look-switcher', SLookSwitcher);
}
